/**
 * Ícone do sistema — Heroicons, traço fino (24×24 outline).
 *
 * Trocado do Material Symbols (arredondado, traço grosso). O Heroicons cobre
 * bem a UI genérica (ação, navegação, estado) mas NÃO tem pictograma de
 * domínio — roupa, tênis, halter, caixa registradora, leitor de código de
 * barras. Onde faltou, o mapa abaixo usa o ícone genérico mais próximo
 * (ex.: "point_of_sale" vira cartão de crédito, não caixa registradora) — é
 * uma aproximação deliberada, registrada aqui para quem for revisar depois.
 *
 * O nome do ícone continua sendo uma união fechada e não uma `string`, pelo
 * mesmo motivo de antes: o TypeScript recusa um ícone fora do conjunto
 * mapeado, em vez de deixar a tela mostrar nada.
 *
 * Nenhuma tela precisou mudar nesta troca: só este arquivo conhece o
 * componente real por trás de cada `NomeIcone`.
 */
import type { ComponentType, SVGProps } from 'react'
import {
  ArchiveBoxArrowDownIcon,
  ArchiveBoxIcon,
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
  ArrowRightOnRectangleIcon,
  ArrowTopRightOnSquareIcon,
  ArrowTrendingDownIcon,
  ArrowTrendingUpIcon,
  BanknotesIcon,
  BellIcon,
  BoltIcon,
  BuildingStorefrontIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  CheckCircleIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  DocumentArrowUpIcon,
  DocumentDuplicateIcon,
  Cog6ToothIcon,
  CreditCardIcon,
  EllipsisVerticalIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  FireIcon,
  FlagIcon,
  InformationCircleIcon,
  ListBulletIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  PercentBadgeIcon,
  PhotoIcon,
  PlusCircleIcon,
  PlusIcon,
  PrinterIcon,
  QrCodeIcon,
  ReceiptPercentIcon,
  ShieldCheckIcon,
  ShoppingBagIcon,
  ShoppingCartIcon,
  Squares2X2Icon,
  TableCellsIcon,
  TagIcon,
  TruckIcon,
  UserGroupIcon,
  UserIcon,
  UserPlusIcon,
  WalletIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'

export type NomeIcone =
  | 'add'
  | 'add_box'
  | 'apparel'
  | 'arrow_back'
  | 'abre_em_nova_aba'
  | 'bar_chart'
  | 'sino'
  | 'barcode_scanner'
  | 'calendario'
  | 'check'
  | 'check_circle'
  | 'checkroom'
  | 'chevron_esquerda'
  | 'chevron_direita'
  | 'atraso'
  | 'baixar'
  | 'close'
  | 'copiar'
  | 'documento_importar'
  | 'edit_note'
  | 'error'
  | 'imprimir'
  | 'fitness_center'
  | 'grid_view'
  | 'group'
  | 'image'
  | 'info'
  | 'inventory_2'
  | 'local_shipping'
  | 'lock'
  | 'logout'
  | 'more_vert'
  | 'payments'
  | 'percent'
  | 'person'
  | 'person_add'
  | 'person_search'
  | 'point_of_sale'
  | 'receipt_long'
  | 'report'
  | 'search'
  | 'sell'
  | 'settings'
  | 'shield'
  | 'shopping_cart'
  | 'space_dashboard'
  | 'sprint'
  | 'store'
  | 'storefront'
  | 'sync'
  | 'trending_down'
  | 'trending_up'
  | 'view_list'
  | 'visibility'
  | 'warning'

type ComponenteSVG = ComponentType<SVGProps<SVGSVGElement>>

/**
 * Mapa nome → componente. Comentado só onde o Heroicons não tem o desenho
 * literal e a escolha exige explicação.
 */
const COMPONENTE: Record<NomeIcone, ComponenteSVG> = {
  add: PlusIcon,
  add_box: PlusCircleIcon,
  apparel: ShoppingBagIcon, // sem ícone de roupa: sacola é o mais próximo
  arrow_back: ArrowLeftIcon,
  abre_em_nova_aba: ArrowTopRightOnSquareIcon,
  bar_chart: ChartBarIcon,
  sino: BellIcon,
  barcode_scanner: QrCodeIcon, // sem leitor de código de barras: QR Code é o mais próximo
  calendario: CalendarDaysIcon,
  check: CheckIcon,
  check_circle: CheckCircleIcon,
  checkroom: ArchiveBoxArrowDownIcon, // "uso interno": item que sai do estoque para dentro da loja
  chevron_esquerda: ChevronLeftIcon,
  chevron_direita: ChevronRightIcon,
  atraso: ClockIcon,
  baixar: ArrowDownTrayIcon,
  close: XMarkIcon,
  copiar: DocumentDuplicateIcon,
  documento_importar: DocumentArrowUpIcon,
  edit_note: PencilSquareIcon,
  error: ExclamationCircleIcon,
  imprimir: PrinterIcon,
  fitness_center: FireIcon, // sem halter: usa o ícone de "energia/esforço"
  grid_view: TableCellsIcon,
  group: UserGroupIcon,
  image: PhotoIcon,
  info: InformationCircleIcon,
  inventory_2: ArchiveBoxIcon,
  local_shipping: TruckIcon,
  lock: LockClosedIcon,
  logout: ArrowRightOnRectangleIcon,
  more_vert: EllipsisVerticalIcon,
  payments: BanknotesIcon,
  percent: PercentBadgeIcon,
  person: UserIcon,
  person_add: UserPlusIcon,
  person_search: MagnifyingGlassIcon,
  point_of_sale: CreditCardIcon, // sem caixa registradora: cartão é o mais próximo do pagamento no balcão
  receipt_long: ReceiptPercentIcon,
  report: FlagIcon,
  search: MagnifyingGlassIcon,
  sell: TagIcon,
  settings: Cog6ToothIcon,
  shield: ShieldCheckIcon,
  shopping_cart: ShoppingCartIcon,
  space_dashboard: Squares2X2Icon,
  sprint: BoltIcon, // sem corredor: usa o ícone de "rápido"
  // `store` é a identidade da loja (rodapé da barra lateral, menu da conta);
  // `storefront` é a tela de Caixa — precisam de desenhos diferentes, senão
  // "onde eu opero" e "a loja em si" viram a mesma coisa na tela.
  store: BuildingStorefrontIcon,
  storefront: WalletIcon,
  sync: ArrowPathIcon,
  trending_down: ArrowTrendingDownIcon,
  trending_up: ArrowTrendingUpIcon,
  view_list: ListBulletIcon,
  visibility: EyeIcon,
  warning: ExclamationTriangleIcon,
}

export function Icone({
  nome,
  tamanho = 20,
  peso = 400,
  className = '',
}: {
  nome: NomeIcone
  tamanho?: number
  /**
   * O Heroicons outline não tem variação de peso — é sempre o mesmo traço de
   * 1.5px. A prop continua existindo para nenhuma tela precisar mudar; só
   * decide se o traço fica um pouco mais grosso (`strokeWidth`) para o
   * pedido de ênfase que já existia (o check do item marcado, por exemplo).
   */
  peso?: 400 | 500 | 600 | 700
  className?: string
}) {
  const Componente = COMPONENTE[nome]
  const espessura = peso >= 600 ? 2 : 1.5

  return (
    <Componente
      aria-hidden
      width={tamanho}
      height={tamanho}
      strokeWidth={espessura}
      className={`inline-block shrink-0 align-[-0.125em] ${className}`}
    />
  )
}
